import { useState } from 'react';
import { apiAdminService } from '@api';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useParams } from 'react-router-dom';
import { TUserFilter, TUser } from '@xeppt/xeppt-sdk/types';
import { useApiMutation } from './useApiMutation';
import { useLocales } from '@hooks/helpers/useLocales';
import { useGlobalModals } from '@hooks/modals/useGlobalModals';
import useModalContext from '@hooks/context/useModalContext';

interface IProps {
    isInitialAdmins?: boolean;
    isInitialAdmin?: boolean;
}

export const useAdmins = ({ isInitialAdmins = false, isInitialAdmin = false }: IProps) => {
    const { id } = useParams();
    const { handleOpenApproveActionModal } = useGlobalModals();
    const { onClose, handleChangeLoading } = useModalContext();
    const { requestSuccessLocale, requestErrorLocale } = useLocales();
    const [filter, setFilter] = useState<TUserFilter>({
        search: undefined,
        status: undefined,
        order: 'ASC'
    });

    const { data: admins, handleRequest: refetchAdmins } = useApiQuery({
        method: () => apiAdminService.getUserList(filter),
        isInitialRequest: isInitialAdmins,
        deps: [filter]
    });

    const { data: admin } = useApiQuery({
        method: () => apiAdminService.getUserById(id || ''),
        condition: !!id,
        deps: [id],
        isInitialRequest: isInitialAdmin
    });

    const { handleRequest: handleResendInvite } = useApiMutation({
        method: (id?: string) => apiAdminService.resendInvitationEmail(id || ''),
        onSuccess: () => {
            requestSuccessLocale('resend_invite_admin');
            handleChangeAdminsFilter(filter);
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleDeleteAdmin } = useApiMutation({
        method: (id?: string) => {
            handleChangeLoading(true);
            return apiAdminService.deleteUser(id || '');
        },
        onSuccess: () => {
            onClose();
            requestSuccessLocale('delete_admin');
            handleChangeAdminsFilter(filter);
        },
        onError: requestErrorLocale,
        onFinish: () => handleChangeLoading(false)
    });

    const handleChangeAdminsFilter = (data: Partial<typeof filter>) => {
        setFilter((state) => ({ ...state, ...data }));
    };

    const handleOpenDeleteAdminModal = (data: TUser) => {
        handleOpenApproveActionModal({
            title: 'Delete admin user',
            description: `Are you sure you want to delete ${data.profile.first_name} ${data.profile.last_name}?`,
            onSubmit: () => handleDeleteAdmin(data.id),
            cancelLabel: 'cancel',
            confirmLabel: 'delete'
        });
    };

    return {
        admins,
        refetchAdmins,
        admin,
        handleChangeAdminsFilter,
        handleResendInvite,
        handleDeleteAdmin: handleOpenDeleteAdminModal,
        filter
    };
};
